import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, Typography, Grid, Box, CircularProgress, Button } from "@mui/material";
import getFormConfig from "../../../components/application/universal/formConfig";
import axios from "axios";
import dateFormat from "dateformat";
import _get from "lodash/get";
import { resolveIdToName } from "../../../utilities/detailedViewHelpers";
import DetailedField from "../../../components/detailedApplication/common/DetailedField";
import LoanID from "../../../components/detailedApplication/LoanID";
import ApprovalStatus from "../../../components/detailedApplication/ApprovalStatus";
import DetailedGroupCard from "../../../components/detailedApplication/common/DetailedGroupCard";
import DetailedMultiGroupTable from "../../../components/detailedApplication/common/DetailedMultiGroupTable";
import ScheduleTable from "../../../components/detailedApplication/scheduleTable/ScheduleTable";
import SetDetailedApplicationData from "../../../utilities/SetDetailedApplicationData";
import ScheduleTolerancyDateSetter from "../../../components/detailedApplication/ScheduleTolerancyDateSetter";
import ApprovalHistoryTable from "../../../components/detailedApplication/ApprovalHistoryTable";
import RelatedApplicationsSummary from "../../../components/detailedApplication/RelatedApplicationsSummary"; // Import the new component
import UniversalTransportActivitySum from "../../../components/detailedApplication/UniversalTransportActivitySum";
import PaymentTable from "../../../components/detailedApplication/paymentTable/PaymentTable";
import BankPaymentTable from "../../../components/detailedApplication/bankPaymentTable/BankPaymentTable";
import { Check, Edit } from "@material-ui/icons";
import { Error } from "@mui/icons-material";
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle";
import { downloadPDF, generatePDF, htmlTemplates } from "../../../utilities/PdfUtility";
import PrintIcon from "@mui/icons-material/Print";
import InternalAttachments from "../../../components/detailedApplication/internalAttachments/InternalAttachments";
import AttachmentViewer from "../../../components/attachments/AttachmentViewer";
import { PostData } from "../../../utilities/PostData";

const fetchGeneralInfo = async () => {
  try {
    const response = await axios.get("/general_info");
    return response?.data || {};
  } catch (error) {
    console.error("Error fetching general info:", error);
    return {};
  }
};


function NewUniversalDetailed(props) {
  const { id } = useParams();
  const [applicationInformation, setApplicationInformation] = useState(null);
  const [generalInfo, setGeneralInfo] = useState(null);
  const [formConfig, setFormConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canModifyScheduling, setCanModifyScheduling] = useState(false);
  const [agencyLevel, setAgencyLevel] = useState();
  const [isBank, setIsBank] = useState();
  const [isBankHead, setIsBankHead] = useState();

  const submitApprovalHandler = (approvalStatus) => {
    let comment;
    let rejectionComment;
    if (!approvalStatus) {
      rejectionComment = prompt("უარყოფის მიზეზი: ");
    }
    if (approvalStatus && agencyLevel < 1) {
      comment = prompt("კომენტარი: ");
    }
    axios
      .post("agency/approve_new_universal_application", {
        application_id: props.match.params.id,
        approval: approvalStatus,
        rejection_comment: rejectionComment,
        comment: comment,
      })
      .then((response) => {
        alert(response?.data?.message);
        window.location.reload();
      })
      .catch((error) => {
        console.error("Error submitting approval:", error);
        alert("Error submitting approval");
      });
  };

  const suspendApplicationHandler = () => {
    let suspensionReason = prompt("გაუქმების მიზეზი: ");
    if (!suspensionReason) return;
    if (!window.confirm("გაუქმების დადასტურება")) return;

    let request = { application_type: "new_universal", application_id: props.match.params.id, suspension_reason: suspensionReason };
    PostData("agency/suspend_application", request).then((response) => {
      alert(response?.message);
      window.location.reload();
    });
  };

  useEffect(() => {
    SetDetailedApplicationData({
      setIsBank,
      setIsBankHead,
      setCanModifyScheduling,
      setAgencyLevel,
      setApplicationInformation,
      id: props.match.params.id,
      setLoading,
      applicationType: "new_universal",
    });
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setError(null);
      try {
        const [genInfo] = await Promise.all([fetchGeneralInfo()]);
        setGeneralInfo(genInfo);
        const isLeasing = applicationInformation?.application_type?.name?.toLowerCase().includes("leasing");
        setFormConfig(getFormConfig(genInfo, isLeasing));
      } catch (err) {
        console.error("Failed to load data:", err);
        setError(err.message || "დაფიქსირდა შეცდომა");
      } finally {
      }
    };
    loadData();
  }, [id]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Box sx={{ padding: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  if (!applicationInformation || !formConfig)
    return (
      <Box sx={{ padding: 3 }}>
        <Typography>მონაცემები არ არის ხელმისაწვდომი.</Typography>
      </Box>
    );

  const allGroups = formConfig.groups;
  const allFields = formConfig.fields;

  // Helper function to get field label from formConfig
  const getFieldLabel = (fieldName) => {
    const field = allFields.find((f) => f.name === fieldName);
    if (field) return field.label;

    const group = allGroups.find((g) => g.name === fieldName);
    return group ? group.label : fieldName;
  };

  const formatFieldLabel = (fieldName) => {
    // Check for array index pattern: property.index
    const arrayMatch = fieldName.match(/^(.+)\.(\d+)$/);
    if (arrayMatch) {
      const baseName = arrayMatch[1];
      const index = parseInt(arrayMatch[2]) + 1;
      const baseLabel = getFieldLabel(baseName);
      return `${baseLabel} #${index}`;
    }

    // Check for array item property: property.index.subProperty
    const arrayItemMatch = fieldName.match(/^(.+)\.(\d+)\.(.+)$/);
    if (arrayItemMatch) {
      const baseName = arrayItemMatch[1];
      const index = parseInt(arrayItemMatch[2]) + 1;
      const subProp = arrayItemMatch[3];
      const baseLabel = getFieldLabel(baseName);
      return `${baseLabel} #${index} - ${subProp}`;
    }

    return getFieldLabel(fieldName);
  };

  const formatUpdateValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "boolean") return value ? "მონიშნულია" : "არ არის მონიშნული";
    if (typeof value === "object") {
      const cleanObj = { ...value };
      delete cleanObj._id;

      if (Object.keys(cleanObj).length === 0) return "-";

      return (
        <Box sx={{ ml: 1, mt: 0.5 }}>
          {Object.entries(cleanObj).map(([k, v]) => (
            <Typography key={k} variant="caption" display="block" sx={{ fontFamily: "monospace" }}>
              {k}: {String(v)}
            </Typography>
          ))}
        </Box>
      );
    }
    return String(value);
  };

  const renderSpecificGroup = (groupName) => {
    const group = allGroups.find((g) => g.name === groupName);
    if (!group) return null;

    const groupFields = allFields.filter((f) => f.group === groupName);

    if (group.visibility && !group.visibility(applicationInformation, generalInfo)) {
      return null;
    }

    // Special handling for attachments group - use AttachmentViewer
    if (groupName === "attachments") {
      return (
        <AttachmentViewer
          key={groupName}
          attachments={applicationInformation?.attachments}
          title={group.label}
          classes={{
            component: "MuiCard-root MuiCard-outlined",
            cardSummary: "MuiTypography-h6",
            table: "MuiTable-root",
            row: "MuiTableRow-root",
            title: "MuiTableCell-root",
            detail: "MuiTableCell-root",
          }}
          showEmptyMessage={false}
        />
      );
    }

    if (group.isMultiGroup) {
      const dataArray = _get(applicationInformation, group.name, []);
      return <DetailedMultiGroupTable key={group.name} group={group} fields={groupFields} dataArray={dataArray} generalInfo={generalInfo} />;
    } else {
      return <DetailedGroupCard key={group.name} group={group} fields={groupFields} dataContext={applicationInformation} generalInfo={generalInfo} />;
    }
  };

  return (
    <Card variant="outlined" sx={{ m: 2, width: "98%" }}>
      <CardContent>
        <Card sx={{ mb: 2 }} variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, borderBottom: "1px solid #ccc", pb: 0.5 }}>
              განაცხადის ზოგადი ინფორმაცია
            </Typography>
            <Grid container spacing={1}>
              <Grid container item xs={12} sx={{ borderBottom: "1px solid #e0e0e0", py: 1, alignItems: "center" }}>
                <Grid item xs={6}>
                  <Typography variant="body2">სესხის ID:</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "right" }}>
                  <LoanID application={applicationInformation} isBankHead={isBankHead} />
                </Grid>
              </Grid>
              <DetailedField label="ბანკი" value={resolveIdToName(applicationInformation?.bank, generalInfo?.banks)} />
              <DetailedField label="მომხმარებელი" value={applicationInformation?.user?.name || "-"} />
              <DetailedField label="მომხმარებლის ელ-ფოსტა" value={applicationInformation?.user?.email || "-"} />
              <Grid container item xs={12} sx={{ borderBottom: "1px solid #e0e0e0", py: 1, alignItems: "center" }}>
                <Grid item xs={6}>
                  <Typography variant="body2">სააგენტოს დადასტურების სტატუსი:</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                  <Box sx={{ mr: 0.5 }}>{ApprovalStatus({ applicationInformation })?.icon}</Box>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {ApprovalStatus({ applicationInformation })?.name}
                  </Typography>
                </Grid>
              </Grid>
              {applicationInformation?.rejection_comment && <DetailedField label="უარყოფის კომენტარი" value={applicationInformation.rejection_comment} />}
              <DetailedField
                label="შემოსვლის თარიღი"
                value={applicationInformation?.submit_date ? dateFormat(applicationInformation.submit_date, "yyyy-mm-dd", "T") : "-"}
              />
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {!isBank && applicationInformation?.related_applications_summary && applicationInformation?.related_applications && (
              <RelatedApplicationsSummary
                summaryData={applicationInformation.related_applications_summary}
                applicationsData={applicationInformation.related_applications}
                maxValue={20000000}
                currentApplicationStatus={
                  applicationInformation?.suspended
                    ? "suspended"
                    : applicationInformation?.approved === true
                      ? "approved"
                      : applicationInformation?.approved === false
                        ? "rejected"
                        : "review"
                }
                component="new_universal"
              />
            )}
            {!isBank &&
              applicationInformation?.transport_activity_applications_sum &&
              applicationInformation?.project_information?.economic_activity?.some((activity) => activity.activity_code === "49.39.0") && (
                <Box sx={{ mb: 2 }}>
                  <UniversalTransportActivitySum data={applicationInformation.transport_activity_applications_sum} />
                </Box>
              )}
            {allGroups.filter((_, index) => index % 2 === 0).map((group) => renderSpecificGroup(group.name))}
            {!isBank && (
              <Card sx={{ mb: 2 }} variant="outlined">
                <CardContent>
                  <InternalAttachments
                    applicationId={props.match.params.id}
                    component="new_universal"
                    initialFiles={
                      applicationInformation?.internal_attachments
                        ? (Array.isArray(applicationInformation.internal_attachments)
                          ? applicationInformation.internal_attachments
                          : Object.values(applicationInformation.internal_attachments)
                        )
                          .filter((attachment) => attachment && attachment.signed_url)
                          .map((attachment) => ({
                            fileName: attachment.original_path ? attachment.original_path.split("/").pop() : "ფაილი",
                            url: attachment.signed_url,
                          }))
                        : []
                    }
                    externalClasses={{
                      cardSummary: "MuiTypography-h6",
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {applicationInformation?.approval_history?.length && !isBank > 0 ? <ApprovalHistoryTable applicationInformation={applicationInformation} /> : null}
            {allGroups.filter((_, index) => index % 2 !== 0).map((group) => renderSpecificGroup(group.name))}
            {!isBank && <ScheduleTolerancyDateSetter canModifyScheduling={canModifyScheduling} applicationInformation={applicationInformation} />}
            {applicationInformation?.updateHistory?.length > 0  && (
              <Card sx={{ mb: 2, backgroundColor: "#fff9c4" }} variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1, borderBottom: "1px solid #ccc", pb: 0.5 }}>
                    ცვლილებები
                  </Typography>
                  <Grid container spacing={1}>
                    {applicationInformation.updateHistory.map((update, index) => (
                      <Grid container item xs={12} key={index} sx={{ borderBottom: "1px solid #e0e0e0", py: 1 }}>
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            {formatFieldLabel(update.field)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography component="div" variant="body2" sx={{ fontSize: "0.875rem" }}>
                            <strong>ძველი:</strong> {formatUpdateValue(update.from)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography component="div" variant="body2" sx={{ fontSize: "0.875rem" }}>
                            <strong>ახალი:</strong> {formatUpdateValue(update.to)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            {dateFormat(update.changedAt, "yyyy-mm-dd HH:MM")}
                            {update.changedBy?.name && ` • ${update.changedBy.name}`}
                          </Typography>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
        {applicationInformation?.schedules?.length > 0 ? <ScheduleTable rows={applicationInformation.schedules} /> : null}
        {applicationInformation?.payments?.length > 0 ? <PaymentTable rows={applicationInformation.payments} /> : null}
        {applicationInformation?.bank_payment_history?.length > 0 ? <BankPaymentTable rows={applicationInformation.bank_payment_history} /> : null}
        {isBank && (
          <Box sx={{ mt: 2, mb: 2, display: "flex", justifyContent: "center" }}>
            <Button
              disabled={applicationInformation?.approved || applicationInformation?.suspended}
              onClick={() => {
                props.history.push(`/edit-application/new-universal/${props.match.params.id}`);
              }}
              variant="contained"
              color="primary"
              startIcon={<Edit />}
              sx={{ px: 4, py: 1 }}
            >
              რედაქტირება
            </Button>
          </Box>
        )}
        <Box sx={{ mt: 4, mb: 1, display: "flex", justifyContent: "center", gap: 4 }}>
          {!isBank && agencyLevel !== 0 && typeof applicationInformation?.approved === "undefined" ? (
            <>
              <Button
                onClick={() => submitApprovalHandler(true)}
                variant="contained"
                color="success"
                startIcon={<Check />}
                disabled={loading}
                sx={{ px: 4, py: 1 }}
              >
                დადასტურება
              </Button>
              <Button
                onClick={() => submitApprovalHandler(false)}
                variant="contained"
                color="error"
                startIcon={<Error />}
                disabled={loading}
                sx={{ px: 4, py: 1 }}
              >
                უარყოფა
              </Button>
            </>
          ) : null}
          {applicationInformation?.approved && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              disabled={loading}
              sx={{ px: 4, py: 1 }}
              onClick={() =>
                generatePDF(htmlTemplates.generateNewUniversalConfirmed(applicationInformation, generalInfo, formConfig))
                  .then((pdfBlob) => downloadPDF(pdfBlob))
                  .then(() => console.log("pdf downloaded"))
                  .catch((err) => {
                    console.error(err);
                    alert("დაფიქსირდა შეცდომა");
                  })
              }
            >
              ბეჭდვა
            </Button>
          )}
          {!isBank && agencyLevel >= 2 && !applicationInformation?.suspended ? (
            <>
              <Button
                onClick={() => suspendApplicationHandler()}
                variant="contained"
                color="primary"
                // className={classes.button}
                startIcon={<RemoveCircleIcon />}
                disabled={loading}
              >
                სესხის გაუქმება
              </Button>
            </>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
}

export default NewUniversalDetailed;
